import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { MembersApi } from "@/lib/api";

export function MembersPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId);
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({ queryKey: ["members", pid], queryFn: () => MembersApi.list(pid), enabled: Number.isFinite(pid) });

  const add = useMutation({
    mutationFn: () => MembersApi.add(pid, username.trim()),
    onSuccess: () => {
      setUsername("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["members", pid] });
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? "Failed to add member");
    },
  });

  const remove = useMutation({
    mutationFn: (userId: number) => MembersApi.remove(pid, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", pid] }),
  });

  return (
    <>
      <PageHeader title="Members" subtitle="Project owner can invite contributors by username." />

      <div className="max-w-3xl space-y-6 px-4 py-4 md:px-8 md:py-6">
        <div className="card p-4 md:p-5">
          <form
            className="flex flex-col gap-2 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (!username.trim()) return;
              add.mutate();
            }}
          >
            <div className="flex-1 space-y-1.5">
              <label className="text-xs font-medium text-text-muted">Username</label>
              <input
                className="input"
                placeholder="alice"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary" disabled={add.isPending || !username.trim()}>
              <Plus size={16} /> Add member
            </button>
          </form>
          {error && (
            <div className="mt-3 rounded-md border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}
        </div>

        <div className="card overflow-hidden">
          {members.isLoading && <div className="p-6 text-sm text-text-muted">Loading…</div>}

          {/* Desktop table — unchanged at md+. */}
          <div className="hidden md:block">
            <div className="data-row grid-cols-[1fr_1fr_1fr_1fr] text-xs font-semibold uppercase tracking-wider text-text-dim">
              <div>Username</div>
              <div>Display name</div>
              <div>Role</div>
              <div className="text-right">Actions</div>
            </div>
            {members.data?.map((m) => (
              <div key={m.user_id} className="data-row grid-cols-[1fr_1fr_1fr_1fr]">
                <div className="font-medium">{m.username}</div>
                <div className="text-text-muted">{m.display_name ?? "—"}</div>
                <div className="text-text-muted capitalize">{m.role}</div>
                <div className="text-right">
                  {m.role !== "owner" && (
                    <button
                      onClick={() => remove.mutate(m.user_id)}
                      className="rounded p-1.5 text-text-dim hover:bg-bg-elevated hover:text-status-error"
                      title="Remove member"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile cards. */}
          <div className="divide-y divide-border-muted md:hidden">
            {members.data?.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{m.username}</div>
                  <div className="truncate text-sm text-text-muted">
                    {m.display_name ?? "—"} · <span className="capitalize">{m.role}</span>
                  </div>
                </div>
                {m.role !== "owner" && (
                  <button
                    onClick={() => remove.mutate(m.user_id)}
                    className="shrink-0 rounded p-2 text-text-dim hover:bg-bg-elevated hover:text-status-error"
                    title="Remove member"
                    aria-label="Remove member"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
